import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import cx from 'classnames';
import Button from '@narvar/nth-button';
import s from './ReturnConfirmation.module.css';
import Icon from '../Icon';
import {
  sendOrderLabelEmail,
  updateSecondaryEmail,
  updateSecondaryEmailSent,
} from '../../actions/returnConfirmation';
import patternInputValidation from '../../lib/patternInputValidation';
import { EMAIL_ADDRESS_PATTERN } from '../../constants/validationPatterns';
import PreferencesSaveState from '../PreferencesSaveState';
import { a11yPageRedirection } from '../../lib/a11yUtils';

/* Disabling these rules to preserve legacy behavior */
/* eslint-disable jsx-a11y/anchor-is-valid */

const mapStateToProps = ({
  language = {},
  page: { secureMode, productType } = {},
  // TODO: Generate and use method key from returnSubmission.returnProcessInfo
  returnMethods: { selectedMethodKey } = {},
  returnItems: { returnItems = [] } = {},
  returnConfirmation: { enableEmail, primaryEmail, secondaryEmail, secondaryEmailSent } = {},
  returnSubmission: { multiLabel: { packagesCount } = {} } = {},
  returnsSettings: { [productType]: { disableSecondaryEmail = false } = {} } = {},
}) => ({
  enableEmail,
  primaryEmail,
  isSecondaryEmailEnabled: !secureMode && !disableSecondaryEmail,
  secondaryEmail,
  secondaryEmailSent,
  headerMultilabel: language[`returns.step_4.header.${selectedMethodKey}.multilabel`],
  header:
    language[`returns.step_4.header.${selectedMethodKey}`] || language['returns.step_4.header'],
  emailConfirmation:
    language[`returns.step_4.email_confirmation.${selectedMethodKey}`] ||
    language['returns.step_4.email_confirmation'],
  emailSecondaryLink: language['returns.step_4.email_secondary_link'],
  emailSecondaryMessage:
    language[`returns.step_4.email_secondary_confirmation.${selectedMethodKey}`] ||
    language['returns.step_4.email_secondary_confirmation'],
  emailSecondaryButton: language['returns.step_4.email_secondary_send_button'],
  emailPlaceholder: language['returns.step_3.email_placeholder'],
  emailSecondaryLabel: language['returns.step_4.email_secondary_label'],
  pageRedirectionString: language['returns.general.a11y.announce_page_redirection'],
  packagesCount,
  returnItemsCount: returnItems.length,
});

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  enableEmail: PropTypes.bool.isRequired,
  primaryEmail: PropTypes.string.isRequired,
  isSecondaryEmailEnabled: PropTypes.bool.isRequired,
  secondaryEmail: PropTypes.string.isRequired,
  secondaryEmailSent: PropTypes.bool.isRequired,
  header: PropTypes.string,
  headerMultilabel: PropTypes.string,
  emailConfirmation: PropTypes.string,
  emailSecondaryLink: PropTypes.string,
  emailSecondaryMessage: PropTypes.string,
  emailSecondaryButton: PropTypes.string,
  emailPlaceholder: PropTypes.string,
  emailSecondaryLabel: PropTypes.string,
  packagesCount: PropTypes.number,
  returnItemsCount: PropTypes.number,
  pageRedirectionString: PropTypes.string,
};

const defaultProps = {
  header: '',
  headerMultilabel: null,
  emailConfirmation: '',
  emailSecondaryLink: '',
  emailSecondaryMessage: '',
  emailSecondaryButton: '',
  emailPlaceholder: '',
  emailSecondaryLabel: null,
  packagesCount: null,
  returnItemsCount: null,
  pageRedirectionString: null,
};

const ReturnConfirmation = ({
  dispatch,
  enableEmail,
  primaryEmail,
  isSecondaryEmailEnabled,
  secondaryEmail,
  secondaryEmailSent,
  header,
  headerMultilabel,
  emailConfirmation,
  emailSecondaryLink,
  emailSecondaryMessage,
  emailSecondaryButton,
  emailPlaceholder,
  emailSecondaryLabel,
  packagesCount,
  returnItemsCount,
  pageRedirectionString,
}) => {
  const isSecondaryEmailValid = patternInputValidation(secondaryEmail, EMAIL_ADDRESS_PATTERN);
  const [showSecondaryEmail, setShowSecondaryEmail] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    dispatch(sendOrderLabelEmail(secondaryEmail));
    dispatch(updateSecondaryEmailSent(true));
  };

  const headerText =
    packagesCount && headerMultilabel
      ? headerMultilabel.replace('{items}', returnItemsCount).replace('{packages}', packagesCount)
      : header;

  a11yPageRedirection(
    { 'returns.general.a11y.announce_page_redirection': pageRedirectionString },
    headerText,
  );

  return (
    <div className={s.step_4_header_container}>
      <div aria-label="Transaction Success" role="alert">
        <Icon.SuccessCheckmark className="cta-icon" />
      </div>
      <h2
        className="step-4-header"
        dangerouslySetInnerHTML={{ __html: headerText }}
        ariaLive="polite"
      />

      {enableEmail && (
        <div>
          <div className="confirmation-email-description">
            {emailConfirmation} {primaryEmail}.
          </div>

          {isSecondaryEmailEnabled && (
            <React.Fragment>
              {secondaryEmailSent ? (
                <div className="step-4-subheader fs-exclude">
                  {emailSecondaryMessage} {secondaryEmail}
                </div>
              ) : (
                <a
                  className="secondary-email-link"
                  onClick={(event) => {
                    event.preventDefault();
                    setShowSecondaryEmail(!showSecondaryEmail);
                  }}
                  dangerouslySetInnerHTML={{ __html: emailSecondaryLink }}
                  href="#"
                  role="button"
                  aria-expanded={showSecondaryEmail}
                  onKeyDown={(event) => {
                    if (event.keyCode === 32) {
                      // 32 for spacebar
                      event.preventDefault();
                      setShowSecondaryEmail(!showSecondaryEmail);
                    }
                  }}
                />
              )}

              {
                /* eslint-disable jsx-a11y/label-has-for  */
                showSecondaryEmail && !secondaryEmailSent && (
                  <div className={s.secondary_email_container}>
                    <form name="secondaryEmail" onSubmit={handleSubmit}>
                      {emailSecondaryLabel && (
                        <label htmlFor="secondary-email-input">{emailSecondaryLabel}</label>
                      )}
                      <input
                        className={cx(
                          s.secondary_email_input,
                          'secondary-email-input',
                          'fs-exclude',
                        )}
                        id="secondary-email-input"
                        type="email"
                        placeholder={emailPlaceholder}
                        value={secondaryEmail}
                        onChange={(event) => {
                          dispatch(updateSecondaryEmail(event.target.value));
                        }}
                        required
                        aria-label={emailPlaceholder || 'Secondary email address'}
                      />
                      <Button
                        disabled={!isSecondaryEmailValid || !secondaryEmail}
                        className={cx(
                          s.secondary_email_button,
                          'secondary-email-button',
                          'return-button',
                          'btn',
                          'narvar-btn-primary',
                          'return-button-text',
                        )}
                        s={{ fontSize: '14px', marginLeft: '15px', marginBottom: '3px' }}
                        onClick={handleSubmit}
                      >
                        {emailSecondaryButton}
                      </Button>
                    </form>
                  </div>
                )
              }
            </React.Fragment>
          )}
          <PreferencesSaveState />
        </div>
      )}
    </div>
  );
};

ReturnConfirmation.propTypes = propTypes;
ReturnConfirmation.defaultProps = defaultProps;
export { ReturnConfirmation, mapStateToProps };
export default connect(mapStateToProps)(ReturnConfirmation);
